import { AccessoryType, EventItemAttributes } from '@/types'
import { CreateEventItemMutation } from './__generated__/create_mutation'
import { Mutation as CreateMutation } from './create_mutation'
import { useForm } from '@/helpers/useForm'
import { useEffect, useState } from 'react'
import { Query as TableQuery } from './table_query'
import { Query as OptionalQuery } from './accessory_optional_query'
import { ErrorMessage, Field, Label } from '../base/fieldset'
import { Selector as ItemSelector } from './selector'
import { Selector as StageSelector } from '../event_stage/selector'
import { useQuery } from '@apollo/client'
import { AccessoryOptionalQuery, AccessoryOptionalQueryVariables } from './__generated__/accessory_optional_query'
import { Switch } from '../base/switch'
import { Input } from '../base/input'
import { useInfiniteScroll } from '@/helpers/useInfiniteScroll'
import { dedupe } from '@/helpers/dedupe'
import { useTable } from '@/helpers/useTable'
import { Text } from '../base/text'
import { Inline as Loading } from '@/components/loading'

interface Props {
  eventId: string
  onSuccess: () => void
}

const accessoryTypeMap = {
  AssetType: AccessoryType.AssetType,
  RowItem: AccessoryType.RowItem,
}

export const Form = ({ eventId, onSuccess }: Props) => {
  const {
    attributes,
    setAttributes,
    fieldErrors,
    save,
    saveSuccess,
    formBuilder,
    mutationLoading
  } = useForm<CreateEventItemMutation, EventItemAttributes, never, never>({
    defaultValues: {
      eventId,
      eventStageId: '',
      itemId: '',
      itemType: '',
      notes: '',
      discount: 0,
      quantity: 1,
      accessories: [],
    },
    mutation: CreateMutation,
    resultKey: 'createEventItem',
    refetchQueries: [TableQuery]
  })

  const [filters, setFilters] = useState<AccessoryOptionalQueryVariables['filters']>()

  const { data, loading, fetchMore } = useQuery<AccessoryOptionalQuery>(OptionalQuery, { variables: { filters: { ...filters, optionalEq: true } } })

  const { pageInfo } = useTable({ pageInfo: data?.accessories.pageInfo })

  const { setLoaderElement } = useInfiniteScroll({
    fetchMore: () => fetchMore({
      variables: { after: pageInfo.current?.endCursor },
      updateQuery(previousData, { fetchMoreResult }) {
        if (Object.keys(previousData).length === 0) return fetchMoreResult
        fetchMoreResult.accessories.nodes = dedupe([
          ...previousData.accessories.nodes,
          ...fetchMoreResult.accessories.nodes
        ])
        return { ...fetchMoreResult }
      }
    })
  })

  useEffect(() => {
    if (!data) return

    setAttributes(attributes => ({
      ...attributes, accessories: data.accessories.nodes.map(a => (
        {
          accessoryId: a.accessory.id,
          accessoryType: accessoryTypeMap[a.accessory.__typename || 'AssetType'],
          quantity: a.quantity
        }
      ))
    }))
  }, [data, setAttributes])

  useEffect(() => { if (saveSuccess) onSuccess() }, [saveSuccess, onSuccess])

  return (
    <div className="flex flex-col gap-6">
      { formBuilder.buildQueryError() }
      <Field>
        <Label>Item</Label>
        <ItemSelector
          invalid={!!fieldErrors?.itemId}
          value={`${attributes.itemType}:${attributes.itemId}`}
          onChange={value => {
            const itemId = value?.id?.split(':')[1] || ''
            const itemType = value?.id?.split(':')[0] || ''

            setFilters({ assetTypeIdEq: itemId })

            setAttributes({ ...attributes, itemId, itemType })
          }}
        />
        { fieldErrors?.itemId && <ErrorMessage>{ fieldErrors?.itemId }</ErrorMessage> }
      </Field>
      { !loading && attributes.itemId !== '' && ((data?.accessories?.nodes?.length || 0) !== 0) && (
        <div className="flex flex-col gap-3">
          <Field>
            <Label>Optional accessories</Label>
          </Field>
          <div className="max-h-48 bg-zinc-800 p-4 rounded-lg overflow-y-auto flex flex-col gap-4 border border-zinc-950/50 dark:border-white/10">
            {
              data?.accessories.nodes.map(a => {
                const attrAcc = attributes.accessories?.find(acc => acc.accessoryId == a.accessory.id)
                return (
                  <div className="grid grid-cols-5 gap-4 items-center">
                    <Field>
                      <Label>{a.accessory.name}</Label>
                    </Field>
                    <Switch
                      checked={!!attrAcc}
                      onChange={v => {
                        if (v) {
                          setAttributes({ ...attributes, accessories: [...attributes.accessories || [], { accessoryId: a.accessory.id, accessoryType: accessoryTypeMap[a.accessory.__typename || 'AssetType'], quantity: a.quantity }] })
                        } else {
                          setAttributes({ ...attributes, accessories: attributes.accessories.filter(acc => acc.accessoryId !== a.accessory.id) })
                        }
                      }}
                    />
                    <Input
                      className="col-span-3"
                      disabled={!attrAcc}
                      min={0}
                      step={1}
                      type="number"
                      value={attrAcc?.quantity || 0}
                      onChange={v => {
                        setAttributes({
                          ...attributes, accessories: attributes.accessories.map(att => {
                            if (att.accessoryId == a.accessory.id) {
                              att.quantity = parseInt(v.currentTarget.value)
                            }
                            return att
                          })
                        })
                      }}
                    />

                  </div>
                )
              })
            }
            {
              pageInfo.current?.hasNextPage
                ? <div className="flex p-4 items-center justify-center w-full">
                  <div ref={setLoaderElement} className="flex p-4 items-center justify-center">
                    <Loading />
                  </div>
                </div>
                : data?.accessories.nodes.length == 0
                  ? <></>
                  : <div className="flex p-4 items-center justify-center w-full">
                    <div className="w-full flex flex-row justify-center items-center gap-6">
                      <Text>That's all we've got for now</Text>
                    </div>
                  </div>
            }
          </div>
        </div>
      ) }
      <Field>
        <Label>Stage</Label>
        <StageSelector
          eventId={eventId}
          invalid={!!fieldErrors?.eventStageId}
          value={attributes.eventStageId}
          onChange={value => {
            setAttributes({ ...attributes, eventStageId: value?.id || '' })
          }}
        />
        { fieldErrors?.eventStageId && <ErrorMessage>{ fieldErrors?.eventStageId }</ErrorMessage> }
      </Field>
      <div className="grid grid-cols-2 gap-6">
        { formBuilder.buildField({ name: 'Quantity', field: 'quantity', type: 'number' }) }
        { formBuilder.buildField({ name: 'Discount', field: 'discount', type: 'percentage' }) }
      </div>
      { formBuilder.buildField({ name: 'Notes', field: 'notes', type: 'textarea' }) }
      { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
    </div>
  )
}
